
import { Box, Typography } from '@mui/material';
import { styles } from './styles';
import moment from 'moment';
import ExcelJS from "exceljs";
import React from 'react';
const PERF_TABS = [
    { label: "Today", key: "TODAY" },
    { label: "Yesterday", key: "YESTERDAY" },
    { label: "Current Week", key: "CURRENT_WEEK" },
    // { label: "Previous Week", key: "PREVIOUS_WEEK" },
    // { label: "Current Month", key: "CURRENT_MONTH" },
    // { label: "Previous Month", key: "PREVIOUS_MONTH" },
];

const dateInputStyle = {
    height: 32, padding: '0 8px', borderRadius: 6,
    border: '1.5px solid #e5e7eb', fontSize: 13,
    color: '#374151', background: '#fff', cursor: 'pointer',
    outline: 'none',
};

const getDateRangeFromTab = (tab) => {
    const fmt = (d) => d.format("YYYY-MM-DD");
    switch (tab) {
        case 'Today':
            return { from: fmt(moment()), to: fmt(moment()) };
        case 'Yesterday':
            return { from: fmt(moment().subtract(1, 'days')), to: fmt(moment().subtract(1, 'days')) };
        case 'Current Week':
            return { from: fmt(moment().startOf('isoWeek')), to: fmt(moment().endOf('isoWeek')) };
        case 'Previous Week':
            return { from: fmt(moment().subtract(1, 'week').startOf('isoWeek')), to: fmt(moment().subtract(1, 'week').endOf('isoWeek')) };
        case 'Current Month':
            return { from: fmt(moment().startOf('month')), to: fmt(moment().endOf('month')) };
        case 'Previous Month':
            return { from: fmt(moment().subtract(1, 'month').startOf('month')), to: fmt(moment().subtract(1, 'month').endOf('month')) };
        default:
            return { from: fmt(moment()), to: fmt(moment()) };
    }
};

// Renders a single pagination button
const PaginationBtn = ({ onClick, disabled, active, label }) => (
    <Box
        component="button"
        onClick={onClick}
        disabled={disabled}
        sx={{
            minWidth: 32, height: 32, borderRadius: '6px',
            border: active ? '1.5px solid #1a3a7c' : '1.5px solid #e5e7eb',
            background: active ? '#1a3a7c' : disabled ? '#f9fafb' : '#fff',
            color: active ? '#fff' : disabled ? '#cbd5e1' : '#374151',
            fontSize: 13, fontWeight: active ? 700 : 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
            '&:hover': !disabled && !active ? { background: '#f0f4ff', borderColor: '#1a3a7c' } : {},
        }}
    >
        {label}
    </Box>
);

// Returns array like [0, 1, 2, '...', 8] for smart truncation
const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    if (current < 4) return [0, 1, 2, 3, 4, '...', total - 1];
    if (current > total - 5) return [0, '...', total - 5, total - 4, total - 3, total - 2, total - 1];
    return [0, '...', current - 1, current, current + 1, '...', total - 1];
};

const getMarksForMissedPercentage = (missedPercentage) => {
    const missed = Number(missedPercentage ?? 0);
    if (missed < 5) return 20;
    if (missed >= 5 && missed <= 15) return 15;
    return 0;
};

const EmergeancyPerformanceStatus = ({ data, activeTab, onTabChange, page, onPageChange, pageSize, onPageSizeChange }) => {
    const allRows = Array.isArray(data) ? data : [];
    const totalCount = allRows.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Slice for current page — frontend pagination
    const rows = allRows.slice(page * pageSize, (page + 1) * pageSize);

    const totalZones = data && data.length;
    const totalVehicles = data && data.length > 0 && data.reduce((s, r) => s + r.totalVehicles, 0);
    const totalSchedules = data && data.length > 0 && data.reduce((s, r) => s + r.totalSchedules, 0);
    const totalCompleted = data && data.length > 0 && data.reduce((s, r) => s + r.completedSchedules, 0);
    const avgCompletedPerVehicle = totalVehicles > 0 ? (totalCompleted / totalVehicles).toFixed(1) : '0.0';
    const totalMissed = totalSchedules - totalCompleted;

    // Add this state inside PerformanceStatus component
    const [fromDate, setFromDate] = React.useState('');
    const [toDate, setToDate] = React.useState('');
    const [customActive, setCustomActive] = React.useState(false);

    const handleApplyCustom = () => {
        if (!fromDate || !toDate) return;
        setCustomActive(true);
        onTabChange({ label: "Custom", key: "CUSTOM", fromDate, toDate });
    };

    // Reset custom flag when a PERF_TAB is clicked
    const handleTabClick = (tab) => {
        setCustomActive(false);
        onTabChange(tab);
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Performance Report");

        // ── Column widths ──
        worksheet.columns = [
            { width: 6 },
            { width: 35 },
            { width: 18 },
            { width: 14 },
            { width: 12 },
            { width: 15 },
        ];

        // ── Title row ──
        worksheet.mergeCells("A1:F1");
        const titleCell = worksheet.getCell("A1");
        // titleCell.value = `Performance Status — ${activeTab?.label ?? ""}`;
        titleCell.value = `Performance Status ${activeTab?.label ?? ""} (${moment().format("DD-MM-YYYY")})`;
        titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E40AF" } };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getRow(1).height = 35;

        // ── Summary labels row ──
        const summaryLabels = ["Total FPL", "Total Vehicles", "Total Schedules Trips", "Total Completed Trips", "Total Missed Trips", "Avg Trips/Vehicle"];
        const summaryValues = [totalZones, totalVehicles, totalSchedules, totalCompleted, totalMissed, avgCompletedPerVehicle];

        const labelRow = worksheet.addRow(summaryLabels);
        labelRow.height = 22;
        labelRow.eachCell((cell) => {
            cell.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF334155" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        const valueRow = worksheet.addRow(summaryValues);
        valueRow.height = 22;
        valueRow.eachCell((cell) => {
            cell.font = { bold: true, size: 11, color: { argb: "FF1A3A7C" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFF6FF" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
                top: { style: "thin", color: { argb: "FFBFDBFE" } },
                bottom: { style: "thin", color: { argb: "FFBFDBFE" } },
                left: { style: "thin", color: { argb: "FFBFDBFE" } },
                right: { style: "thin", color: { argb: "FFBFDBFE" } },
            };
        });

        // ── Spacer ──
        worksheet.addRow([]);

        // ── Table header row ──
        const tableHeader = worksheet.addRow(["#", "Total FPL", "Total Schedules", "Completed", "Missed", "Completion %"]);
        tableHeader.height = 22;
        tableHeader.eachCell((cell) => {
            cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A3A7C" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
                top: { style: "thin", color: { argb: "FFCCCCCC" } },
                bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
                left: { style: "thin", color: { argb: "FFCCCCCC" } },
                right: { style: "thin", color: { argb: "FFCCCCCC" } },
            };
        });

        // ── Data rows ──
        allRows.forEach((row, i) => {
            const pct = Number(row.completedPercentage ?? 0);
            const missed = (row.totalSchedules - row.completedSchedules) ?? 0;
            const isEven = i % 2 === 0;

            const dataRow = worksheet.addRow([
                i + 1,
                row.siteName,
                row.totalSchedules ?? 0,
                row.completedSchedules ?? 0,
                missed,
                parseFloat(pct.toFixed(1)),
            ]);
            dataRow.height = 20;

            dataRow.eachCell((cell, colNumber) => {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isEven ? "FFF8FAFF" : "FFFFFFFF" } };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFE2E8F0" } },
                    bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
                    left: { style: "thin", color: { argb: "FFE2E8F0" } },
                    right: { style: "thin", color: { argb: "FFE2E8F0" } },
                };
                cell.alignment = { horizontal: colNumber <= 2 ? "left" : "center", vertical: "middle" };
                cell.font = { size: 10 };

                if (colNumber === 4) cell.font = { size: 10, bold: true, color: { argb: "FF16A34A" } }; // Completed → green
                if (colNumber === 5) cell.font = { size: 10, bold: true, color: { argb: "FFDC2626" } }; // Missed → red
                if (colNumber === 6) {
                    const color = pct >= 50 ? "FF16A34A" : pct >= 30 ? "FFD97706" : "FFDC2626";
                    cell.font = { size: 10, bold: true, color: { argb: color } };
                    cell.numFmt = '0.0"%"';
                }
            });
        });

        // ── Download ──
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `performance_${activeTab?.label ?? "export"}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <Box sx={styles.perfCard}>
            {/* Header — unchanged */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 30 }}>🌐</Typography>
                    <Typography sx={styles.sectionTitle}>Performance Status Schedule Trip Wise</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, justifyContent: "flex-end" }}>

                    {PERF_TABS.map((tab) => (
                        <Box
                            key={tab.key}
                            component="button"
                            onClick={() => handleTabClick(tab)}
                            sx={activeTab?.key === tab.key ? styles.perfTabActive : styles.perfTabInactive}
                        >
                            {tab.label}
                        </Box>
                    ))}
                    {/* <Box
                        component="button"
                        onClick={exportToExcel}
                        sx={{
                            px: 2, height: 32, borderRadius: '6px',
                            border: '1.5px solid #16a34a',
                            background: '#f0fdf4', color: '#16a34a',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            '&:hover': { background: '#dcfce7' },
                        }}
                    >
                        ⬇ Export Excel
                    </Box> */}
                    <input
                        type="date"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={(e) => { setFromDate(e.target.value); setCustomActive(false); }}
                        style={dateInputStyle}
                    />
                    <input
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        onChange={(e) => { setToDate(e.target.value); setCustomActive(false); }}
                        style={dateInputStyle}
                    />
                    <Box
                        component="button"
                        onClick={handleApplyCustom}
                        disabled={!fromDate || !toDate}
                        sx={{
                            px: 2, height: 32, borderRadius: '6px',
                            border: '1.5px solid #1a3a7c',
                            background: customActive ? '#1a3a7c' : '#f0f4ff',
                            color: customActive ? '#fff' : '#1a3a7c',
                            fontSize: 13, fontWeight: 600, cursor: !fromDate || !toDate ? 'not-allowed' : 'pointer',
                            opacity: !fromDate || !toDate ? 0.5 : 1,
                            '&:hover': fromDate && toDate ? { background: '#1a3a7c', color: '#fff' } : {},
                        }}
                    >
                        Apply
                    </Box>
                </Box>
            </Box>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px', justifyContent: "space-between", margin: "10px 0px" }}>
                <div className="pill">Total FPL<span>{totalZones}</span></div>
                <div className="pill">Total Vehicles <span>{totalVehicles}</span></div>
                <div className="pill">Total Schedules Trips <span>{totalSchedules}</span></div>
                <div className="pill completed">Total Completed Trips <span style={{ color: 'green' }}>{totalCompleted}</span></div>
                <div className="pill missed">Total Missed Trips <span style={{ color: 'red' }}>{totalMissed}</span></div>
                <div className="pill">
                    Avg Trips Per Vehicle <span>{avgCompletedPerVehicle}</span>
                </div>
            </div>

            {/* Table header — unchanged */}
            <Box sx={{
                // height: "600px", minHeight: "550px",
                //  overflowY: "auto",
            }}>
                <Box sx={styles.emPerfHeaderRow}>
                    <Typography sx={styles.perfHeaderCell}>#</Typography>
                    <Typography sx={styles.perfHeaderCell}>FPL</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>TOTAL SCHEDULES</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>VEHICLES</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>COMPLETED</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>MISSED/PENDING</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        COMPLETION %
                        <Box
                            component="span"
                            sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover .completion-tooltip': {
                                    opacity: 1,
                                    visibility: 'visible',
                                },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 14, height: 14, borderRadius: '50%',
                                    border: '1.5px solid #94a3b8', color: '#94a3b8',
                                    fontSize: 10, fontWeight: 700,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    lineHeight: 1,
                                }}
                            >
                                i
                            </Box>
                            <Box
                                className="completion-tooltip"
                                sx={{
                                    position: 'absolute',
                                    bottom: '140%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: '#1e293b',
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    whiteSpace: 'nowrap',
                                    opacity: 0,
                                    visibility: 'hidden',
                                    transition: 'opacity 0.15s ease',
                                    zIndex: 10,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    textAlign: 'left',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        border: '5px solid transparent',
                                        borderTopColor: '#1e293b',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '2px' }}>
                                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                                    ≥ 75% Completion
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                                    &lt; 75% Completion
                                </Box>
                            </Box>
                        </Box>
                    </Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>MISSED/PENDING %</Typography>
                    <Typography sx={{ ...styles.perfHeaderCell, textAlign: 'center' }}>MARKS</Typography>
                </Box>
                {/* Rows */}
                {rows.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontSize: 14 }}>
                        {'No data available'}
                    </Box>
                ) : (
                    rows && Array.isArray(rows) && rows.length > 0 && rows.map((row, index) => {
                        const missedPct = 100 - Number(row?.completedPercentage ?? 0);
                        const marks = getMarksForMissedPercentage(missedPct);
                        return (

                            <Box key={row.siteId} sx={styles.emPerfRow(index)}>
                                <Typography sx={styles.perfIndexCell}>
                                    {page * pageSize + index + 1}
                                </Typography>
                                <Typography
                                    sx={{
                                        ...styles.perfZoneCell,
                                        cursor: "pointer",
                                        color: "#1a3a7c",
                                        textDecoration: "underline",
                                    }}
                                    onClick={() => {
                                        const { from, to } = activeTab?.key === 'CUSTOM'
                                            ? { from: activeTab.fromDate, to: activeTab.toDate }
                                            : getDateRangeFromTab(activeTab?.label);
                                        window.open(
                                            `/tripSchedule?siteId=${row.siteId}&siteName=${encodeURIComponent(row.siteName)}&fromDate=${from}&toDate=${to}`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    {row?.siteName}
                                </Typography>
                                <Typography sx={{ ...styles.perfTripsCell, textAlign: 'center' }}>
                                    {row?.totalSchedules ?? 0}
                                </Typography>
                                <Typography sx={{ ...styles.perfVehiclesCell, textAlign: 'center', color: "black" }}>
                                    {row?.totalVehicles ?? 0}
                                </Typography>

                                <Typography sx={{ ...styles.perfTripsCell, textAlign: 'center', color: "green" }}>
                                    {row?.completedSchedules ?? 0}
                                </Typography>
                                <Typography sx={{ ...styles.perfVehiclesCell, textAlign: 'center', color: "red" }}>
                                    {row?.totalSchedules - row?.completedSchedules ?? 0}
                                </Typography>


                                <Typography sx={{
                                    ...styles.perfAvgCell,
                                    textAlign: 'center',
                                    color: row?.completedPercentage >= 90 ? '#16a34a' : '#ef4444',
                                    fontWeight: 700,
                                }}>
                                    {Number(row?.completedPercentage ?? 0).toFixed(1)}%
                                </Typography>

                                <Typography sx={{
                                    ...styles.perfAvgCell,
                                    textAlign: 'center',
                                    color: 100- row?.completedPercentage <= 10 ? '#16a34a' : "#ef4444",
                                    fontWeight: 700,
                                }}>
                                    {

                                        (100 - Number(row?.completedPercentage ?? 0).toFixed(1)).toFixed(1)
                                    }%
                                </Typography>
                                <Typography sx={{
                                    ...styles.perfAvgCell,
                                    textAlign: 'center',
                                    color: marks >= 15 ? '#16a34a' : marks === 0 ? '#ef4444' : '#f59e0b',
                                    fontWeight: 700,
                                    fontSize: 14,
                                }}>
                                    {marks}
                                </Typography>
                            </Box>
                        )

                    }))
                }

            </Box>

            {/* ── Pagination bar ── */}
            {totalCount > 0 && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: "space-between", gap: "20px",
                    mt: 2, pt: 2, borderTop: '1px solid #f0f0f0',
                }}>
                    {/* Left: Marking Guide Info */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.5, background: '#f0f9ff', border: '1.5px solid #bfdbfe', borderRadius: '8px' }}>
                        {/* <Typography sx={{ fontSize: 13, color: '#1a3a7c', fontWeight: 700 }}>
                            📊 Marking Criteria
                        </Typography> */}
                        <Typography sx={{ fontSize: 14, color: '#374151' }}>
                            Missing trips % &lt; 5% <span style={{ fontWeight: 700, color: '#16a34a' }}> : 20 Marks</span>
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: '#374151' }}>
                            Missing trips % 5-15% <span style={{ fontWeight: 700, color: '#f59e0b' }}> : 15 Marks</span>
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: '#374151' }}>
                            Missing trips % &gt; 15% <span style={{ fontWeight: 700, color: '#ef4444' }}> : 0 Marks</span>
                        </Typography>
                    </Box>
                    {/* Right: prev / page numbers / next */}
                    {totalPages > 1 && (
                        <div style={{ display: "flex", gap: 20 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
                                    Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
                                </Typography>

                                {/* ── Rows per page dropdown ── */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Rows per page:</Typography>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => {
                                            onPageSizeChange(Number(e.target.value));
                                            onPageChange(0);
                                        }}
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: '#374151',
                                            border: '1.5px solid #e5e7eb',
                                            borderRadius: 6,
                                            padding: '4px 8px',
                                            background: '#fff',
                                            cursor: 'pointer',
                                            outline: 'none',
                                        }}
                                    >
                                        {[10, 15, 20, 50, 100].map(n => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PaginationBtn
                                    onClick={() => onPageChange(page - 1)}
                                    disabled={page === 0}
                                    label="←"
                                />
                                {getPageNumbers(page, totalPages).map((p, i) =>
                                    p === '...' ? (
                                        <Typography key={`ellipsis-${i}`} sx={{ px: 1, color: '#94a3b8', fontSize: 13 }}>
                                            …
                                        </Typography>
                                    ) : (
                                        <PaginationBtn
                                            key={p}
                                            onClick={() => onPageChange(p)}
                                            active={p === page}
                                            label={p + 1}
                                        />
                                    )
                                )}
                                <PaginationBtn
                                    onClick={() => onPageChange(page + 1)}
                                    disabled={page >= totalPages - 1}
                                    label="→"
                                />
                            </Box>
                        </div>
                    )}
                </Box>
            )}


        </Box>
    );
};
export default EmergeancyPerformanceStatus